import { mergeSessions } from "./mergeSessions";
import {
  DataPatch,
  WizardSession,
  WizardSessionsSnapshot,
  WizardSessionsState,
} from "./types";

export type SessionKeyGetter = (data: object) => string | undefined;

type StartSessionFields = {
  sessionId: string;
  wizardType: string;
  firstStepId: string;
  initialData: object;
  params?: unknown;
  getSessionKey?: SessionKeyGetter;
  now: number;
};

type ScopeFields = {
  userID: string;
  envID: string;
  snapshot: WizardSessionsSnapshot;
};

export type WizardSessionsAction =
  | ({ type: "open" } & StartSessionFields)
  | ({ type: "restart"; replacedSessionId: string } & StartSessionFields)
  | { type: "expand"; sessionId: string }
  | { type: "minimize" }
  | { type: "discard"; sessionId: string; now: number }
  | { type: "updateData"; sessionId: string; patch: DataPatch; now: number }
  | {
      type: "goToStep";
      sessionId: string;
      stepId: string;
      completedStepId?: string;
      now: number;
    }
  | {
      type: "complete";
      sessionId: string;
      completedStepId: string;
      now: number;
    }
  | { type: "recordApplicableSteps"; sessionId: string; stepIds: string[] }
  | ({ type: "hydrate" } & ScopeFields)
  /** Applies sessions another tab saved for the same scope. */
  | ({ type: "merge" } & ScopeFields)
  | { type: "reset" };

export const initialWizardSessionsState: WizardSessionsState = {
  userID: null,
  envID: null,
  sessions: {},
  discarded: {},
  expandedId: null,
};

/** Sessions of a wizard that share the key derived from `data`. */
export function findMatchingSessions(
  sessions: Record<string, WizardSession>,
  wizardType: string,
  data: object,
  getSessionKey?: SessionKeyGetter,
): WizardSession[] {
  const key = getSessionKey?.(data);
  return Object.values(sessions).filter(
    (session) =>
      session.type === wizardType && getSessionKey?.(session.data) === key,
  );
}

function withoutSessions(
  state: WizardSessionsState,
  sessionIds: string[],
  now: number,
): WizardSessionsState {
  const present = sessionIds.filter((id) => state.sessions[id]);
  if (present.length === 0) {
    return state;
  }
  const sessions = { ...state.sessions };
  const discarded = { ...state.discarded };
  for (const id of present) {
    delete sessions[id];
    discarded[id] = now;
  }
  return {
    ...state,
    sessions,
    discarded,
    expandedId:
      state.expandedId && present.includes(state.expandedId)
        ? null
        : state.expandedId,
  };
}

/**
 * Summoning a wizard that is already in progress for the same key resumes it
 * rather than throwing away the user's progress. Finished sessions with the
 * key are replaced by a fresh one.
 */
function openSession(
  state: WizardSessionsState,
  fields: StartSessionFields,
): WizardSessionsState {
  const matches = findMatchingSessions(
    state.sessions,
    fields.wizardType,
    fields.initialData,
    fields.getSessionKey,
  );
  const inProgress = matches.find((session) => session.completedAt === null);
  if (inProgress) {
    return { ...state, expandedId: inProgress.id };
  }

  const cleared = withoutSessions(
    state,
    matches.map((session) => session.id),
    fields.now,
  );
  return {
    ...cleared,
    sessions: {
      ...cleared.sessions,
      [fields.sessionId]: {
        id: fields.sessionId,
        type: fields.wizardType,
        currentStepId: fields.firstStepId,
        completedStepIds: [],
        data: fields.initialData,
        params: fields.params,
        startedAt: fields.now,
        updatedAt: fields.now,
        completedAt: null,
      },
    },
    expandedId: fields.sessionId,
  };
}

function withCompleted(session: WizardSession, stepId?: string): string[] {
  if (!stepId || session.completedStepIds.includes(stepId)) {
    return session.completedStepIds;
  }
  return [...session.completedStepIds, stepId];
}

function updateSession(
  state: WizardSessionsState,
  sessionId: string,
  update: (session: WizardSession) => WizardSession,
): WizardSessionsState {
  const session = state.sessions[sessionId];
  if (!session) {
    return state;
  }
  return {
    ...state,
    sessions: { ...state.sessions, [sessionId]: update(session) },
  };
}

function sameList(a: string[] | undefined, b: string[]): boolean {
  return (
    !!a &&
    a.length === b.length &&
    a.every((value, index) => value === b[index])
  );
}

function sameRecord<T>(a: Record<string, T>, b: Record<string, T>): boolean {
  const keys = Object.keys(a);
  return (
    keys.length === Object.keys(b).length &&
    keys.every((key) => a[key] === b[key])
  );
}

function mergeFromOtherTab(
  state: WizardSessionsState,
  { userID, envID, snapshot }: ScopeFields,
): WizardSessionsState {
  if (state.userID !== userID || state.envID !== envID) {
    return state;
  }
  const merged = mergeSessions(state, snapshot);
  if (
    sameRecord(merged.sessions, state.sessions) &&
    sameRecord(merged.discarded, state.discarded)
  ) {
    return state;
  }
  return {
    ...state,
    ...merged,
    expandedId:
      state.expandedId && merged.sessions[state.expandedId]
        ? state.expandedId
        : null,
  };
}

export function wizardSessionsReducer(
  state: WizardSessionsState,
  action: WizardSessionsAction,
): WizardSessionsState {
  switch (action.type) {
    case "open":
      return openSession(state, action);
    case "restart":
      return openSession(
        withoutSessions(state, [action.replacedSessionId], action.now),
        action,
      );
    case "expand":
      if (!state.sessions[action.sessionId]) {
        return state;
      }
      return { ...state, expandedId: action.sessionId };
    case "minimize":
      return { ...state, expandedId: null };
    case "discard":
      return withoutSessions(state, [action.sessionId], action.now);
    case "updateData":
      return updateSession(state, action.sessionId, (session) => ({
        ...session,
        data: {
          ...session.data,
          ...(typeof action.patch === "function"
            ? action.patch(session.data)
            : action.patch),
        },
        updatedAt: action.now,
      }));
    case "goToStep":
      return updateSession(state, action.sessionId, (session) => ({
        ...session,
        currentStepId: action.stepId,
        completedStepIds: withCompleted(session, action.completedStepId),
        updatedAt: action.now,
      }));
    case "complete":
      return updateSession(state, action.sessionId, (session) => ({
        ...session,
        completedStepIds: withCompleted(session, action.completedStepId),
        completedAt: action.now,
        updatedAt: action.now,
      }));
    case "recordApplicableSteps":
      if (
        sameList(
          state.sessions[action.sessionId]?.applicableStepIds,
          action.stepIds,
        )
      ) {
        return state;
      }
      // Leaves updatedAt alone: the list is derived, so it isn't progress.
      return updateSession(state, action.sessionId, (session) => ({
        ...session,
        applicableStepIds: action.stepIds,
      }));
    case "hydrate":
      return {
        userID: action.userID,
        envID: action.envID,
        ...action.snapshot,
        expandedId: null,
      };
    case "merge":
      return mergeFromOtherTab(state, action);
    case "reset":
      return state === initialWizardSessionsState
        ? state
        : initialWizardSessionsState;
  }
}
